import { useLocation, useNavigate } from "react-router-dom";
import { Users, X } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import { useMatchmakerPrivacyNotice } from "@/lib/useMatchmakerPrivacyNotice";

// Routes that already show the equivalent notice inline (Onboarding.js'
// genres step, reached via /app/welcome) or aren't the right place for a
// floating banner mid-flow (ProfileSetup.js) — suppressed here so nobody
// ever sees both surfaces.
const SUPPRESSED_PREFIXES = ["/app/welcome", "/app/profile/setup"];

// One-time notice for EXISTING members about Matchmaker Taste Privacy —
// mounted globally in AppLayout, so it naturally appears on "next app open"
// (whichever /app/* page they land on first) and would just as naturally
// be the first thing they see if that page happens to be Friends. New
// members get the equivalent line during onboarding instead — both mark
// the SAME `matchmaker_privacy_notice_seen` field. Suppressed while the
// legal re-acceptance gate is blocking the app, same as the rest of the
// app shell underneath it.
export function MatchmakerPrivacyNotice() {
  const { user } = useAuth();
  const { seen, markSeen } = useMatchmakerPrivacyNotice();
  const navigate = useNavigate();
  const location = useLocation();
  if (seen || user?.needs_legal_reacceptance) return null;
  if (SUPPRESSED_PREFIXES.some((p) => location.pathname === p || location.pathname.startsWith(`${p}/`))) return null;

  return (
    <div data-testid="matchmaker-privacy-notice"
      className="fixed inset-x-4 bottom-36 z-40 mx-auto flex max-w-md items-start gap-3 rounded-[16px] border border-gold/30 bg-[#0D1117]/95 p-4 shadow-[0_10px_30px_-10px_rgba(0,0,0,0.65)] backdrop-blur-md sm:left-auto sm:right-6 sm:bottom-36 lg:bottom-24">
      <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gold/15 text-gold">
        <Users size={17} weight="fill" />
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-[13.5px] font-semibold text-[#F6F5F1]">Your taste helps friends' Matchmaker picks</p>
        <p className="mt-1 text-[12.5px] leading-snug text-dim">
          By default, your favorite genres and highly-rated titles can be used when a friend picks something for you
          both to watch. You can turn this off anytime.
        </p>
        <button
          data-testid="matchmaker-privacy-notice-manage"
          onClick={() => { markSeen(); navigate("/app/settings/privacy-social"); }}
          className="mt-2.5 text-[12.5px] font-bold text-gold underline-offset-2 hover:underline">
          Manage in Privacy settings
        </button>
      </div>
      <button
        data-testid="matchmaker-privacy-notice-dismiss"
        onClick={markSeen}
        aria-label="Dismiss"
        className="shrink-0 rounded-full p-1 text-faint transition-colors hover:bg-white/[0.06] hover:text-dim">
        <X size={15} weight="bold" />
      </button>
    </div>
  );
}
